import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCog,
  faServer,
  faDatabase,
  faShield,
  faEnvelope,
  faGlobe,
  faBell,
  faKey,
  faUpload,
  faDownload,
  faSave,
  faUndo,
  faCheck,
  faTimes,
  faExclamationTriangle,
  faInfo,
  faEye,
  faEyeSlash,
  faTrash,
  faPlus,
  faEdit,
  faHistory,
  faRestore
} from '@fortawesome/free-solid-svg-icons';
import { adminAPI } from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  
  // 设置数据
  const [settings, setSettings] = useState({
    general: {
      siteName: '',
      siteDescription: '',
      siteKeywords: '',
      contactEmail: '',
      contactPhone: '',
      address: '',
      timezone: 'Asia/Shanghai',
      language: 'zh-CN',
      dateFormat: 'YYYY-MM-DD',
      timeFormat: '24h'
    },
    security: {
      passwordMinLength: 8,
      passwordRequireUppercase: true,
      passwordRequireLowercase: true,
      passwordRequireNumbers: true,
      passwordRequireSymbols: false,
      sessionTimeout: 24,
      maxLoginAttempts: 5,
      lockoutDuration: 30,
      twoFactorAuth: false,
      ipWhitelist: [],
      allowedFileTypes: ['.jpg', '.jpeg', '.png', '.gif', '.pdf', '.doc', '.docx'],
      maxFileSize: 10
    },
    email: {
      smtpHost: '',
      smtpPort: 587,
      smtpSecure: true,
      smtpUser: '',
      smtpPassword: '',
      fromEmail: '',
      fromName: '',
      replyToEmail: '',
      emailTemplates: {
        welcome: '',
        passwordReset: '',
        contactNotification: ''
      }
    },
    notification: {
      enableEmailNotifications: true,
      enableSmsNotifications: false,
      enablePushNotifications: false,
      notifyOnNewContact: true,
      notifyOnNewUser: true,
      notifyOnSystemError: true,
      adminEmails: [],
      smsApiKey: '',
      pushApiKey: ''
    },
    backup: {
      autoBackup: true,
      backupFrequency: 'daily',
      backupRetention: 30,
      backupLocation: 'local',
      cloudProvider: '',
      cloudCredentials: {}
    },
    api: {
      rateLimit: 100,
      rateLimitWindow: 15,
      enableCors: true,
      corsOrigins: ['*'],
      apiKeys: [],
      webhooks: []
    }
  });

  // 显示/隐藏密码
  const [showPasswords, setShowPasswords] = useState({});
  
  // 新增项目的临时状态
  const [newItems, setNewItems] = useState({
    ipWhitelist: '',
    adminEmail: '',
    corsOrigin: '',
    apiKey: { name: '', permissions: [] },
    webhook: { name: '', url: '', events: [] }
  });

  // 获取设置数据
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminAPI.getSystemSettings();
      if (response.data.status === 'success') {
        setSettings(prev => ({ ...prev, ...response.data.data }));
      }
    } catch (err) {
      setError(err.message || '获取设置失败');
    } finally {
      setLoading(false);
    }
  };

  // 保存设置
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const response = await adminAPI.updateSystemSettings(settings);
      if (response.data.status === 'success') {
        setSuccess('设置保存成功');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.message || '保存设置失败');
    } finally {
      setSaving(false);
    }
  };

  // 重置设置
  const handleReset = async () => {
    if (window.confirm('确定要重置所有设置吗？此操作不可恢复。')) {
      try {
        setSaving(true);
        const response = await adminAPI.resetSystemSettings();
        if (response.data.status === 'success') {
          await fetchSettings();
          setSuccess('设置已重置');
          setTimeout(() => setSuccess(null), 3000);
        }
      } catch (err) {
        setError(err.message || '重置设置失败');
      } finally {
        setSaving(false);
      }
    }
  };

  // 测试邮件配置
  const handleTestEmail = async () => {
    try {
      const response = await adminAPI.testEmailSettings(settings.email);
      if (response.data.status === 'success') {
        setSuccess('邮件测试成功');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.message || '邮件测试失败');
    }
  };

  // 备份数据
  const handleBackup = async () => {
    try {
      const response = await adminAPI.createBackup();
      if (response.data.status === 'success') {
        setSuccess('备份创建成功');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.message || '创建备份失败');
    }
  };

  // 更新设置值
  const updateSetting = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  // 更新嵌套设置值
  const updateNestedSetting = (category, parentKey, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [parentKey]: {
          ...prev[category][parentKey],
          [key]: value
        }
      }
    }));
  };

  // 添加数组项
  const addArrayItem = (category, key, item) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: [...prev[category][key], item]
      }
    }));
  };

  // 删除数组项
  const removeArrayItem = (category, key, index) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: prev[category][key].filter((_, i) => i !== index)
      }
    }));
  };

  // 切换密码显示
  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // 渲染通用设置
  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">基本信息</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              网站名称
            </label>
            <input
              type="text"
              value={settings.general.siteName}
              onChange={(e) => updateSetting('general', 'siteName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              联系邮箱
            </label>
            <input
              type="email"
              value={settings.general.contactEmail}
              onChange={(e) => updateSetting('general', 'contactEmail', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              联系电话
            </label>
            <input
              type="tel"
              value={settings.general.contactPhone}
              onChange={(e) => updateSetting('general', 'contactPhone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              时区
            </label>
            <select
              value={settings.general.timezone}
              onChange={(e) => updateSetting('general', 'timezone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="Asia/Shanghai">Asia/Shanghai</option>
              <option value="UTC">UTC</option>
              <option value="America/New_York">America/New_York</option>
              <option value="Europe/London">Europe/London</option>
            </select>
          </div>
        </div>
        
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            网站描述
          </label>
          <textarea
            value={settings.general.siteDescription}
            onChange={(e) => updateSetting('general', 'siteDescription', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            地址
          </label>
          <textarea
            value={settings.general.address}
            onChange={(e) => updateSetting('general', 'address', e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
    </div>
  );

  // 渲染安全设置
  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">密码策略</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              最小长度
            </label>
            <input
              type="number"
              min="6"
              max="32"
              value={settings.security.passwordMinLength}
              onChange={(e) => updateSetting('security', 'passwordMinLength', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              会话超时（小时）
            </label>
            <input
              type="number"
              min="1"
              max="168"
              value={settings.security.sessionTimeout}
              onChange={(e) => updateSetting('security', 'sessionTimeout', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        
        <div className="mt-6 space-y-4">
          {[
            { key: 'passwordRequireUppercase', label: '要求大写字母' },
            { key: 'passwordRequireLowercase', label: '要求小写字母' },
            { key: 'passwordRequireNumbers', label: '要求数字' },
            { key: 'passwordRequireSymbols', label: '要求特殊字符' },
            { key: 'twoFactorAuth', label: '启用双因素认证' }
          ].map((item) => (
            <div key={item.key} className="flex items-center">
              <input
                type="checkbox"
                id={item.key}
                checked={settings.security[item.key]}
                onChange={(e) => updateSetting('security', item.key, e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor={item.key} className="ml-2 block text-sm text-gray-900">
                {item.label}
              </label>
            </div>
          ))}
        </div>
      </div>
      
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">IP白名单</h3>
        <div className="space-y-3">
          {settings.security.ipWhitelist.map((ip, index) => (
            <div key={index} className="flex items-center space-x-2">
              <input
                type="text"
                value={ip}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              />
              <button
                onClick={() => removeArrayItem('security', 'ipWhitelist', index)}
                className="px-3 py-2 text-red-600 hover:text-red-800"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
          
          <div className="flex items-center space-x-2">
            <input
              type="text"
              placeholder="输入IP地址"
              value={newItems.ipWhitelist}
              onChange={(e) => setNewItems(prev => ({ ...prev, ipWhitelist: e.target.value }))}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={() => {
                if (newItems.ipWhitelist.trim()) {
                  addArrayItem('security', 'ipWhitelist', newItems.ipWhitelist.trim());
                  setNewItems(prev => ({ ...prev, ipWhitelist: '' }));
                }
              }}
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // 渲染邮件设置
  const renderEmailSettings = () => (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">SMTP配置</h3>
          <button
            onClick={handleTestEmail}
            className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
          >
            测试邮件
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMTP主机
            </label>
            <input
              type="text"
              value={settings.email.smtpHost}
              onChange={(e) => updateSetting('email', 'smtpHost', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMTP端口
            </label>
            <input
              type="number"
              value={settings.email.smtpPort}
              onChange={(e) => updateSetting('email', 'smtpPort', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              用户名
            </label>
            <input
              type="text"
              value={settings.email.smtpUser}
              onChange={(e) => updateSetting('email', 'smtpUser', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              密码
            </label>
            <div className="relative">
              <input
                type={showPasswords.smtpPassword ? 'text' : 'password'}
                value={settings.email.smtpPassword}
                onChange={(e) => updateSetting('email', 'smtpPassword', e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('smtpPassword')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <FontAwesomeIcon 
                  icon={showPasswords.smtpPassword ? faEyeSlash : faEye} 
                  className="h-4 w-4 text-gray-400" 
                />
              </button>
            </div>
          </div>
        </div>
        
        <div className="mt-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="smtpSecure"
              checked={settings.email.smtpSecure}
              onChange={(e) => updateSetting('email', 'smtpSecure', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="smtpSecure" className="ml-2 block text-sm text-gray-900">
              使用SSL/TLS加密
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  // 渲染通知设置
  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">通知开关</h3>
        <div className="space-y-4">
          {[
            { key: 'enableEmailNotifications', label: '启用邮件通知' },
            { key: 'enableSmsNotifications', label: '启用短信通知' },
            { key: 'enablePushNotifications', label: '启用推送通知' },
            { key: 'notifyOnNewContact', label: '新联系时通知' },
            { key: 'notifyOnNewUser', label: '新用户注册时通知' },
            { key: 'notifyOnSystemError', label: '系统错误时通知' }
          ].map((item) => (
            <div key={item.key} className="flex items-center">
              <input
                type="checkbox"
                id={item.key}
                checked={settings.notification[item.key]}
                onChange={(e) => updateSetting('notification', item.key, e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor={item.key} className="ml-2 block text-sm text-gray-900">
                {item.label}
              </label>
            </div>
          ))}
        </div>
      </div>
      
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">管理员邮箱</h3>
        <div className="space-y-3">
          {settings.notification.adminEmails.map((email, index) => (
            <div key={index} className="flex items-center space-x-2">
              <input
                type="email"
                value={email}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              />
              <button
                onClick={() => removeArrayItem('notification', 'adminEmails', index)}
                className="px-3 py-2 text-red-600 hover:text-red-800"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
          
          <div className="flex items-center space-x-2">
            <input
              type="email"
              placeholder="输入邮箱地址"
              value={newItems.adminEmail}
              onChange={(e) => setNewItems(prev => ({ ...prev, adminEmail: e.target.value }))}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={() => {
                if (newItems.adminEmail.trim()) {
                  addArrayItem('notification', 'adminEmails', newItems.adminEmail.trim());
                  setNewItems(prev => ({ ...prev, adminEmail: '' }));
                }
              }}
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // 渲染备份设置
  const renderBackupSettings = () => (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">备份配置</h3>
          <button
            onClick={handleBackup}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
          >
            <FontAwesomeIcon icon={faDownload} className="mr-2" />
            立即备份
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              备份频率
            </label>
            <select
              value={settings.backup.backupFrequency}
              onChange={(e) => updateSetting('backup', 'backupFrequency', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="hourly">每小时</option>
              <option value="daily">每天</option>
              <option value="weekly">每周</option>
              <option value="monthly">每月</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              保留天数
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={settings.backup.backupRetention}
              onChange={(e) => updateSetting('backup', 'backupRetention', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        
        <div className="mt-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="autoBackup"
              checked={settings.backup.autoBackup}
              onChange={(e) => updateSetting('backup', 'autoBackup', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="autoBackup" className="ml-2 block text-sm text-gray-900">
              启用自动备份
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* 页面标题和操作 */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">系统设置</h1>
            <p className="mt-1 text-sm text-gray-500">
              配置系统的各项参数和功能
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={handleReset}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faUndo} className="mr-2" />
              重置
            </button>
            
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <FontAwesomeIcon icon={faSave} className="mr-2" />
              )}
              {saving ? '保存中...' : '保存设置'}
            </button>
          </div>
        </div>

        {/* 消息提示 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <FontAwesomeIcon icon={faCheck} className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
              <button
                onClick={() => setSuccess(null)}
                className="ml-auto text-green-400 hover:text-green-600"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        )}

        {/* 标签页导航 */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'general', name: '通用设置', icon: faCog },
              { id: 'security', name: '安全设置', icon: faShield },
              { id: 'email', name: '邮件设置', icon: faEnvelope },
              { id: 'notification', name: '通知设置', icon: faBell },
              { id: 'backup', name: '备份设置', icon: faDownload }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FontAwesomeIcon 
                  icon={tab.icon} 
                  className={`mr-2 h-4 w-4 ${
                    activeTab === tab.id ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`} 
                />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* 内容区域 */}
        <div>
          {activeTab === 'general' && renderGeneralSettings()}
          {activeTab === 'security' && renderSecuritySettings()}
          {activeTab === 'email' && renderEmailSettings()}
          {activeTab === 'notification' && renderNotificationSettings()}
          {activeTab === 'backup' && renderBackupSettings()}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Settings;